import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { MessageCircle, X } from 'lucide-react';

const FloatingWhatsApp = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      // Show button after scrolling 300px
      if (window.scrollY > 300) {
        setIsVisible(true);
      } else {
        setIsVisible(false);
        setIsExpanded(false);
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const whatsappUrl = 'https://wa.me/message/R2S5SGLV5TP5O1';

  return (
    <AnimatePresence>
      {isVisible && (
        <motion.div
          initial={{ opacity: 0, scale: 0, y: 100 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0, y: 100 }}
          transition={{ type: 'spring', stiffness: 260, damping: 20 }}
          style={{
            position: 'fixed',
            bottom: '2rem',
            right: '2rem',
            zIndex: 1000,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'flex-end',
            gap: '0.75rem'
          }}
          data-testid="floating-whatsapp-container"
        >
          {/* Expanded Message Card */}
          <AnimatePresence>
            {isExpanded && (
              <motion.div
                initial={{ opacity: 0, y: 20, scale: 0.8 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                exit={{ opacity: 0, y: 20, scale: 0.8 }}
                transition={{ duration: 0.3 }}
                style={{
                  background: 'linear-gradient(135deg, #1A1A1A 0%, #0A0A0A 100%)',
                  border: '1px solid rgba(212, 175, 55, 0.3)',
                  borderRadius: '1rem',
                  padding: '1.5rem',
                  maxWidth: '280px',
                  boxShadow: '0 10px 40px rgba(0, 0, 0, 0.5), 0 0 20px rgba(212, 175, 55, 0.1)'
                }}
              >
                <button
                  onClick={() => setIsExpanded(false)}
                  style={{
                    position: 'absolute',
                    top: '0.75rem',
                    right: '0.75rem',
                    background: 'transparent',
                    border: 'none',
                    cursor: 'pointer',
                    color: 'rgba(255, 255, 255, 0.5)',
                    padding: '0.25rem'
                  }}
                  aria-label="Close"
                >
                  <X size={16} />
                </button>
                <div style={{ marginBottom: '1rem' }}>
                  <h4 style={{ 
                    color: '#D4AF37', 
                    fontSize: '1rem', 
                    fontWeight: '600', 
                    marginBottom: '0.5rem',
                    fontFamily: "'SF Mono', monospace",
                    textTransform: 'uppercase',
                    letterSpacing: '0.05em'
                  }}>
                    Let's Talk Growth
                  </h4>
                  <p style={{ 
                    color: 'rgba(255, 255, 255, 0.8)', 
                    fontSize: '0.875rem', 
                    lineHeight: '1.5' 
                  }}>
                    Ready to scale your brand? Chat with us directly on WhatsApp.
                  </p>
                </div>
                <motion.a
                  href={whatsappUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  whileHover={{ scale: 1.03 }}
                  whileTap={{ scale: 0.97 }}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '0.5rem',
                    background: 'linear-gradient(135deg, #D4AF37 0%, #F1E5AC 100%)',
                    color: '#000000',
                    padding: '0.75rem 1.25rem',
                    borderRadius: '2rem',
                    textDecoration: 'none',
                    fontWeight: '600',
                    fontSize: '0.875rem',
                    fontFamily: "'SF Mono', monospace",
                    textTransform: 'uppercase',
                    letterSpacing: '0.025em',
                    boxShadow: '0 4px 15px rgba(212, 175, 55, 0.3)'
                  }}
                  data-testid="floating-whatsapp-cta"
                >
                  <MessageCircle size={18} />
                  Start Chat
                </motion.a>
              </motion.div>
            )}
          </AnimatePresence>

          {/* Floating Button */}
          <motion.button
            onClick={() => setIsExpanded(!isExpanded)}
            whileHover={{ scale: 1.1 }}
            whileTap={{ scale: 0.9 }}
            animate={{ 
              boxShadow: isExpanded 
                ? '0 4px 20px rgba(212, 175, 55, 0.4)' 
                : ['0 4px 20px rgba(212, 175, 55, 0.3)', '0 4px 30px rgba(212, 175, 55, 0.5)', '0 4px 20px rgba(212, 175, 55, 0.3)']
            }}
            transition={{ 
              boxShadow: { duration: 2, repeat: Infinity, ease: 'easeInOut' }
            }}
            style={{
              width: '60px',
              height: '60px',
              borderRadius: '50%',
              background: 'linear-gradient(135deg, #D4AF37 0%, #F1E5AC 100%)',
              border: '2px solid rgba(255, 255, 255, 0.2)',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: '#000000'
            }}
            aria-label="Open WhatsApp consultation"
            data-testid="floating-whatsapp-btn"
          >
            <motion.div
              animate={{ rotate: isExpanded ? 0 : [0, -10, 10, -10, 0] }}
              transition={{ duration: 0.5, repeat: isExpanded ? 0 : Infinity, repeatDelay: 3 }}
            >
              {isExpanded ? <X size={24} /> : <MessageCircle size={24} />}
            </motion.div>
          </motion.button>

          {/* Pulse Ring Animation */}
          {!isExpanded && (
            <motion.div
              animate={{
                scale: [1, 1.5, 1.5],
                opacity: [0.5, 0, 0]
              }}
              transition={{
                duration: 2,
                repeat: Infinity,
                ease: 'easeOut'
              }}
              style={{
                position: 'absolute',
                bottom: 0,
                right: 0,
                width: '60px',
                height: '60px',
                borderRadius: '50%',
                border: '2px solid #D4AF37',
                pointerEvents: 'none'
              }}
            />
          )}
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default FloatingWhatsApp;
